// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import { type UpdateWorkOrderBody, type WorkOrder, useEvolveGet } from '@evolvemep/api-ts';

import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import { useWorkOrders } from 'modules/Shop/WorkOrders/WorkOrdersPage/useWorkOrders';

/**
 * Shared update/refresh/permission plumbing for a Work Order detail view — used by both the desktop
 * and mobile detail pages so `editable`/`saving` stay derived the same way in both places.
 */
export const useWorkOrderDetailMutations = (
  workOrder: WorkOrder,
  onWorkOrderUpdated: (workOrder: WorkOrder) => void,
) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const { updateWorkOrder: patchWorkOrder, mutationRunning: savingWorkOrder } = useWorkOrders();

  const updateWorkOrder = useCallback(
    (body: UpdateWorkOrderBody) => patchWorkOrder(workOrder.workOrderId, body).then(onWorkOrderUpdated),
    [onWorkOrderUpdated, patchWorkOrder, workOrder.workOrderId],
  );

  const { apiCall: getWorkOrderApiCall, loading } = useEvolveGet(`shop/workOrder/${workOrder.workOrderId}`);
  const refreshWorkOrder = useCallback(
    async () => getWorkOrderApiCall().then(onWorkOrderUpdated),
    [onWorkOrderUpdated, getWorkOrderApiCall],
  );

  const saving = loading || savingWorkOrder;
  const editable = canEditWorkOrder('editProperties') && !saving;

  return { workOrder, updateWorkOrder, refreshWorkOrder, editable, saving, loading, canEditWorkOrder };
};
